import { Avatar, Card, cn, Icons, Spinner } from "@win-reality/react-ui";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { usePlayers } from "./hooks/usePlayers";

type PlayerSelectionProps = {
  selectedTeamId: string;
};

type SortDirection = "asc" | "desc";

const getInitials = (name: string | undefined): string => {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const PlayerSelection = ({
  selectedTeamId: selectedTeam,
}: PlayerSelectionProps) => {
  const { players, isLoading } = usePlayers(selectedTeam);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const sortedPlayers = useMemo(() => {
    const sorted = [...players].sort((a, b) => {
      const nameA = a.name?.split(" ")[0] || "";
      const nameB = b.name?.split(" ")[0] || "";
      return nameA.localeCompare(nameB);
    });
    return sortDirection === "desc" ? sorted.reverse() : sorted;
  }, [players, sortDirection]);

  const toggleSort = () => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  return (
    <div className="relative flex flex-col h-full">
      <h2 className="text-lg font-bold text-text-light mb-4">Players</h2>

      <button
        type="button"
        onClick={toggleSort}
        className="flex items-center gap-1 text-xs font-medium text-accent-dark-3 uppercase tracking-wide mb-3 hover:text-text-light transition-colors"
      >
        <Icons.ChevronUp
          className={cn(
            "h-3 w-3 transition-transform",
            sortDirection === "desc" && "rotate-180",
          )}
        />
        Name
      </button>

      <Card className="flex-1 overflow-hidden bg-white light:bg-white rounded-md p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner />
          </div>
        ) : players.length > 0 ? (
          <ul
            className="h-[600px] overflow-y-auto space-y-2"
            data-testid="players-list"
          >
            {sortedPlayers.map((player) => (
              <li
                key={player.id}
                className="rounded-md border border-border-input bg-background hover:bg-gray-50 transition-colors"
              >
                <Link
                  to={`/players/${encodeURIComponent(player.id ?? "")}/home`}
                  className="flex items-center gap-3 px-4 py-3"
                  data-testid={`player-${player.id}`}
                >
                  <Avatar
                    variant="default"
                    initials={getInitials(player.name)}
                    className="h-10 w-10 bg-text-light text-text-dark text-sm font-medium"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-blue truncate">
                      {player.name}
                    </div>
                    <div className="text-sm text-accent-dark-3">
                      {player.pitch_side && `Throw: ${player.pitch_side}`}
                      {player.pitch_side && player.hit_side && " "}
                      {player.hit_side && `Bats: ${player.hit_side}`}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center text-accent-dark-3 py-8">
            No players found
          </div>
        )}
      </Card>
    </div>
  );
};
